import { getSearchApiBaseUrl } from '../../apiConfig';
import { getAuthHeader } from '../auth';
import { markSharingOff } from './sharingV2';

// Teams sharing: the Express routes that own every access mutation
// (ExpressApp CLAUDE.md, "Teams Sharing"). They answer SHARING_OFF until the
// env's sharing switch is on.

export type AccessApiKind = 'board' | 'project' | 'collection' | 'schedule' | 'concept';
export type AccessApiLevel = 'none' | 'view' | 'edit';
export type KindCounts = Partial<Record<AccessApiKind, number>>;

export interface AccessListValue {
  everyone?: AccessApiLevel;
  members?: Record<string, 'view' | 'edit'>;
}
export interface GrantOps {
  everyone?: AccessApiLevel;
  set?: Record<string, 'view' | 'edit'>;
  remove?: string[];
}
export interface DerivedArraysValue {
  readers: string[];
  editors: string[];
}

// Codes whose server text is missing or not meant for people.
const MESSAGES: Record<string, string> = {
  CONCEPT_BUSY: 'A concept is still generating. Try again when it finishes.',
  MEMBER_CONCEPT_BUSY: 'One of their concepts is still generating. Try again when it finishes.',
  NOT_ALLOWED: "You don't have permission to change who can see this.",
  NOT_A_TEAMMATE: 'That person is no longer an active member of the team.',
  EVERYONE_OWNER_ONLY: 'Only the owner can share this with everyone.',
  OWNER_ONLY: 'Only the owner can do that.',
  NOT_A_DESIGNER: 'You need a designer seat in the team to move content into it.',
  TEAM_LAPSED: "The team's subscription has ended, so its content is read-only.",
  ALREADY_IN_TEAM: 'This is already in the team.',
  WORKSPACE_CHANGED: 'This moved to another workspace. Reload and try again.',
  CANNOT_MOVE_OUT: "You can't remove this from its project.",
  ADMIN_ONLY: 'Only a team admin can remove members.',
  NOT_ACTIVE: 'Only active members can leave the team this way.',
  NOT_A_MEMBER: "They're no longer a member of this team.",
  PRIMARY_OWNER: "The team's owner can't be removed or leave.",
  SHARING_OFF: "Sharing isn't switched on for this team yet. Try again.",
};

export class AccessApiError extends Error {
  constructor(public code: string, public status: number, serverMessage?: string) {
    super(
      MESSAGES[code] ??
        (serverMessage && serverMessage !== code ? serverMessage : 'Something went wrong. Please try again.'),
    );
    this.name = 'AccessApiError';
  }
}

const call = async <T>(
  method: string,
  path: string,
  body?: unknown,
  team?: string | null,
): Promise<T> => {
  let res: Response;
  try {
    res = await fetch(`${getSearchApiBaseUrl()}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(await getAuthHeader()) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new AccessApiError('NETWORK_ERROR', 0);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (data?.error === 'SHARING_OFF' && team) markSharingOff(team);
    throw new AccessApiError(data?.error || `HTTP_${res.status}`, res.status, data?.message);
  }
  return data as T;
};

const seg = encodeURIComponent;

export const putAccess = (kind: AccessApiKind, id: string, ops: GrantOps, team?: string | null) =>
  call<{ access: AccessListValue; children: number } & DerivedArraysValue>(
    'PUT',
    `/api/access/${kind}/${seg(id)}`,
    ops,
    team,
  );

export const setItemProject = (
  kind: AccessApiKind,
  id: string,
  projectId: string | null,
  team?: string | null,
) =>
  call<{ projectId: string | null } & DerivedArraysValue>(
    'POST',
    `/api/access/${kind}/${seg(id)}/project`,
    { projectId },
    team,
  );

export const moveItemToTeam = (kind: AccessApiKind, id: string, firmId: string) =>
  call<{ firmId: string; moved: Array<{ kind: AccessApiKind; id: string }>; unfiled: Array<{ kind: AccessApiKind; id: string }> }>(
    'POST',
    `/api/access/${kind}/${seg(id)}/move-to-team`,
    { firmId },
    firmId,
  );

export const getProjectDeletePreview = (projectId: string, team?: string | null) =>
  call<{ archive: KindCounts; unfile: KindCounts }>(
    'GET',
    `/api/access/project/${seg(projectId)}/delete-preview`,
    undefined,
    team,
  );

export const deleteProjectWithContents = (projectId: string, team?: string | null) =>
  call<{ archive: KindCounts; unfile: KindCounts }>(
    'DELETE',
    `/api/access/project/${seg(projectId)}`,
    undefined,
    team,
  );

export interface MemberContentSummary {
  /** Null only when nothing transfers and no designer could take it. */
  newOwner: string | null;
  toPersonal: KindCounts;
  transfer: KindCounts;
  grants: KindCounts;
}

export const getMemberContentSummary = (firmId: string, uid: string) =>
  call<MemberContentSummary>(
    'GET',
    `/api/team/${seg(firmId)}/members/${seg(uid)}/content-summary`,
    undefined,
    firmId,
  );

export const removeTeamMember = (firmId: string, uid: string) =>
  call<MemberContentSummary & { childrenRebuilt: number; rowsArchived: number }>(
    'POST',
    `/api/team/${seg(firmId)}/members/${seg(uid)}/remove`,
    undefined,
    firmId,
  );

const NOUNS: Record<AccessApiKind, [string, string]> = {
  board: ['board', 'boards'],
  project: ['project', 'projects'],
  collection: ['collection', 'collections'],
  schedule: ['schedule', 'schedules'],
  concept: ['concept', 'concepts'],
};
const ORDER: AccessApiKind[] = ['project', 'board', 'concept', 'collection', 'schedule'];

// "5 boards, 2 concepts and 1 schedule"; "" when there is nothing.
export const describeCounts = (counts: KindCounts): string => {
  const parts = ORDER.filter((k) => (counts[k] ?? 0) > 0).map((k) => {
    const n = counts[k]!;
    return `${n} ${NOUNS[k][n === 1 ? 0 : 1]}`;
  });
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] ?? '';
};

